import React, { useState, useRef, useCallback, useEffect } from 'react';
import { IconImages, IconArrowsLeftRight, IconArrowRight, IconGift, IconCheck } from './Icons';
import { useLanguage } from '../context/LanguageContext';
import useScrollReveal from '../hooks/useScrollReveal';
import './ProductImagesSection.css';

const IconCube = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const IconComments = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const IconChartBar = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/>
  </svg>
);

const BeforeAfterSlider = ({ beforeImage, afterImage, name, beforeLabel, afterLabel }) => {
  const containerRef = useRef(null);
  const [sliderPos, setSliderPos] = useState(50);
  const isDragging = useRef(false);

  const getPosition = useCallback((clientX) => {
    if (!containerRef.current) return 50;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.min(Math.max((x / rect.width) * 100, 0), 100);
  }, []);

  const handleMove = useCallback((clientX) => {
    if (!isDragging.current) return;
    setSliderPos(getPosition(clientX));
  }, [getPosition]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    setSliderPos(getPosition(e.clientX));
  }, [getPosition]);

  const handleTouchStart = useCallback((e) => {
    isDragging.current = true;
    setSliderPos(getPosition(e.touches[0].clientX));
  }, [getPosition]);

  useEffect(() => {
    const onMouseMove = (e) => handleMove(e.clientX);
    const onTouchMove = (e) => handleMove(e.touches[0].clientX);
    const onEnd = () => { isDragging.current = false; };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [handleMove]);

  return (
    <div ref={containerRef} className="ba-slider" onMouseDown={handleMouseDown} onTouchStart={handleTouchStart}>
      <img src={afterImage} alt={`${name} ${afterLabel}`} className="ba-slider__img ba-slider__img--after" draggable={false} />
      <img src={beforeImage} alt={`${name} ${beforeLabel}`} className="ba-slider__img ba-slider__img--before" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }} draggable={false} />
      <span className="ba-slider__label ba-slider__label--before">{beforeLabel}</span>
      <span className="ba-slider__label ba-slider__label--after">{afterLabel}</span>
      <div className="ba-slider__divider" style={{ left: `${sliderPos}%` }}>
        <div className="ba-slider__handle">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </div>
      </div>
    </div>
  );
};

const ProductImagesSection = () => {
  const { t } = useLanguage();
  const [activeSlider, setActiveSlider] = useState(4);

  const sliders = [
    { name: 'Aquaone', before: '/img/vornach/Aquaone/aquaone%20vorher.webp', after: '/img/vornach/Aquaone/Aquaone%20nachher.webp' },
    { name: 'BB-Verpackungen', before: '/img/vornach/BB-Verpackungen/bb%20vorher.webp', after: '/img/vornach/BB-Verpackungen/bb%20nachher.webp' },
    { name: 'Buxtrade', before: '/img/vornach/Buxtrade/buxtrade%20vorher.webp', after: '/img/vornach/Buxtrade/Buxtrade%20nachher.webp' },
    { name: 'Gräfenhof', before: '/img/vornach/Graefenhof/graefenhof-vorher.webp', after: '/img/vornach/Graefenhof/graefenhof-nachher.webp' },
    { name: 'McFilter', before: '/img/vornach/McFilter/McFilter%20vorher.webp', after: '/img/vornach/McFilter/McFilter%20nachher.webp' },
    { name: 'Oswald', before: '/img/vornach/Oswald/oswald%20vorher.webp', after: '/img/vornach/Oswald/Oswald%20nachher.webp' },
    { name: 'Papierdrachen', before: '/img/vornach/Papierdrachen/Papierdrachen%20vorher.webp', after: '/img/vornach/Papierdrachen/Papierdrachen%20nachher.webp' },
    { name: 'Yosana', before: '/img/vornach/Yosana/Yosana%20vorher.webp', after: '/img/vornach/Yosana/Yosana%20nachher.webp' },
  ];

  const highlights = [
    { icon: 'cube', text: t.productImages.highlight1 },
    { icon: 'comments', text: t.productImages.highlight2 },
    { icon: 'chart-bar', text: t.productImages.highlight3 },
    { icon: 'gift', text: t.productImages.highlight4 },
  ];

  const getHighlightIcon = (iconType) => {
    switch(iconType) {
      case 'cube': return <IconCube size={16} />;
      case 'comments': return <IconComments size={16} />;
      case 'chart-bar': return <IconChartBar size={16} />;
      case 'gift': return <IconGift size={16} />;
      default: return <IconCheck size={16} />;
    }
  };

  const visualReveal = useScrollReveal({ threshold: 0.1 });
  const textReveal = useScrollReveal({ threshold: 0.15 });

  return (
    <section className="product-images-fox" id="produktbilder">
      <div className="product-images-fox__bg-orb product-images-fox__bg-orb--1"></div>
      <div className="product-images-fox__bg-orb product-images-fox__bg-orb--2"></div>
      <div className="product-images-fox__bg-orb product-images-fox__bg-orb--3"></div>
      <div className="product-images-fox__bg-grid"></div>
      
      <div className="product-images-fox__container">
        <div className="product-images-fox__content">
          <div ref={visualReveal.ref} className={`product-images-fox__visual reveal--left ${visualReveal.isRevealed ? 'reveal--visible' : ''}`}>
            <div className="product-images-fox__image-wrapper">
              <div className="product-images-fox__slider-hint">
                <IconArrowsLeftRight size={18} />
                <span>{t.productImages.sliderHint}</span>
              </div>
              <div className="product-images-fox__slider-container">
                <BeforeAfterSlider
                  key={activeSlider}
                  beforeImage={sliders[activeSlider].before}
                  afterImage={sliders[activeSlider].after}
                  name={sliders[activeSlider].name}
                  beforeLabel={t.productImages.beforeLabel}
                  afterLabel={t.productImages.afterLabel}
                />
              </div>
              <div className="product-images-fox__slider-buttons">
                {sliders.map((slider, index) => (
                  <button
                    key={index}
                    className={`product-images-fox__slider-btn ${activeSlider === index ? 'product-images-fox__slider-btn--active' : ''}`}
                    onClick={() => setActiveSlider(index)}
                  >
                    {slider.name}
                  </button>
                ))}
              </div>
              <div className="product-images-fox__image-glow"></div>
            </div>
          </div>

          <div ref={textReveal.ref} className={`product-images-fox__text-content reveal--right ${textReveal.isRevealed ? 'reveal--visible' : ''}`}>
            <div className="product-images-fox__label">
              <IconImages size={22} />
              {t.productImages.label}
            </div>
            <h2 className="product-images-fox__title">
              {t.productImages.title} <span className="product-images-fox__title-gradient">{t.productImages.titleHighlight}</span>
            </h2>
            <p className="product-images-fox__tagline">{t.productImages.tagline}</p>
            <p className="product-images-fox__description">{t.productImages.description}</p>

            <div className="product-images-fox__highlights">
              <span className="product-images-fox__highlights-label">{t.productImages.highlightsLabel}</span>
              <div className="product-images-fox__highlights-grid">
                {highlights.map((item, index) => (
                  <div key={index} className="product-images-fox__highlight">
                    {getHighlightIcon(item.icon)}
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <button className="product-images-fox__cta-button">
              {t.productImages.cta}
              <span className="product-images-fox__cta-icon"><IconArrowRight size={16} /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductImagesSection;
